import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Trash2,
  Download,
  TrendingDown,
  FileText,
  Search,
  Save,
  Check,
  MessageSquare,
  DollarSign,
  Clock,
  CheckCircle2,
  CreditCard,
  Calendar,
  Lock,
  Pencil,
  Plus,
  Zap,
  History,
  Eye,
  SlidersHorizontal,
  Send,
  ShieldCheck,
  Copy,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import MemberLayout from "@/layouts/MemberLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ListPagination } from "@/components/ui/list-pagination";
import {
  useListCostEstimates,
  useDeleteCostEstimate,
  getListCostEstimatesQueryKey,
  useListMemberEstimates,
  useUpdateMemberEstimateStatus,
  useUpdateCostEstimateDetails,
  useListMasterCptCodes,
} from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { exportEstimateToCSV } from "@/lib/estimateExporter";

const COLORS = ["#0ea5e9", "#f1f5f9"];

export default function SavedEstimates() {
  const [activeTab, setActiveTab] = useState<"all" | "received" | "my">("all");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [limit, setLimit] = useState(10);
  const {
    data: memberEstimates,
    isLoading: memberEstimatesLoading,
    refetch: refetchMemberEstimates,
  } = useListMemberEstimates();
  const updateMemberStatus = useUpdateMemberEstimateStatus();
  const updateEstimateDetails = useUpdateCostEstimateDetails();
  const { data: estimates, isLoading: estimatesLoading } = useListCostEstimates(
    {},
  );
  const { data: masterCptData } = useListMasterCptCodes({ limit: 5000 });
  const masterCptList: any[] = useMemo(
    () =>
      masterCptData?.items ||
      (Array.isArray(masterCptData) ? masterCptData : []),
    [masterCptData],
  );
  const deleteEstimate = useDeleteCostEstimate();
  const [deletingId, setDeletingId] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [proposalModalOpen, setProposalModalOpen] = useState(false);
  const [proposalEstimate, setProposalEstimate] = useState<any>(null);
  const [proposedPrice, setProposedPrice] = useState("");
  const [proposedNote, setProposedNote] = useState("");

  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submitEstimate, setSubmitEstimate] = useState<any>(null);
  const [submitNote, setSubmitNote] = useState("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState<any>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDiscount, setEditDiscount] = useState<number>(0);
  const [editDiscountAmount, setEditDiscountAmount] = useState<number>(0);
  const [editItems, setEditItems] = useState<
    { code: string; description: string; quantity: number; unitPrice: number }[]
  >([]);

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyEstimate, setHistoryEstimate] = useState<any>(null);

  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [voucherEstimate, setVoucherEstimate] = useState<any>(null);

  const handleOpenVoucherModal = (est: any) => {
    setVoucherEstimate(est);
    setVoucherModalOpen(true);
  };

  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundEstimate, setRefundEstimate] = useState<any>(null);
  const [refundReason, setRefundReason] = useState("");

  const handleOpenRefundModal = (est: any) => {
    setRefundEstimate(est);
    setRefundReason("");
    setRefundModalOpen(true);
  };

  const handleProcessRefund = async () => {
    if (!refundEstimate) return;
    try {
      await updateMemberStatus.mutateAsync({
        id: String(refundEstimate.id),
        status: "REFUNDED",
        finalAmount: Number(refundEstimate.finalAmount || 0),
        negotiationNote: `Refund processed before provider voucher redemption. Reason: ${refundReason || "Patient requested refund before service rendering."}`,
      });
      toast({
        title: "Refund Processed & Voucher Cancelled",
        description: `Your estimate payment of $${Number(refundEstimate.finalAmount || 0).toFixed(2)} has been refunded and the service voucher was voided.`,
      });
      setRefundModalOpen(false);
      setRefundEstimate(null);
      setRefundReason("");
      refetchMemberEstimates();
      await queryClient.invalidateQueries({
        queryKey: getListCostEstimatesQueryKey(),
      });
    } catch (err: any) {
      toast({
        title: "Refund Error",
        description: err.message || "Failed to process refund",
        variant: "destructive",
      });
    }
  };

  const handleOpenSubmitModal = (est: any) => {
    setSubmitEstimate(est);
    setSubmitNote("");
    setSubmitModalOpen(true);
  };

  const handleSubmitToProvider = async () => {
    if (!submitEstimate) return;
    try {
      await updateMemberStatus.mutateAsync({
        id: String(submitEstimate.id),
        status: "SUBMITTED",
        negotiationNote:
          submitNote ||
          "Submitted Good Faith Estimate for provider review & negotiation.",
        targetEstimate: submitEstimate,
      });
      toast({
        title: "Estimate Sent to Provider!",
        description:
          "Your Good Faith Estimate and negotiation notes have been sent to the provider.",
      });
      setSubmitModalOpen(false);
      setSubmitEstimate(null);
      setSubmitNote("");
      refetchMemberEstimates();
      await queryClient.invalidateQueries({
        queryKey: getListCostEstimatesQueryKey(),
      });
    } catch (err: any) {
      toast({
        title: "Submission Error",
        description: err.message || "Failed to send estimate to provider",
        variant: "destructive",
      });
    }
  };

  const handleOpenEditModal = (est: any) => {
    setEditingEstimate(est);
    setEditTitle(est.name || est.title || "Custom Cost Estimate");
    const initDiscountPct = Number(est.discount || 0);
    setEditDiscount(initDiscountPct);
    const rawItems = Array.isArray(est.items) ? est.items : [];
    let mappedItems = [];
    if (rawItems.length > 0) {
      mappedItems = rawItems.map((item: any) => ({
        code: item.code || item.cptCode || "CUSTOM",
        description: item.description || item.name || "Medical Service",
        quantity: Number(item.quantity || 1),
        unitPrice: Number(item.unitPrice || item.price || 0),
      }));
    } else {
      mappedItems = [
        {
          code: est.cptCodes || "CUSTOM",
          description: est.procedure || est.name || "Medical Service",
          quantity: 1,
          unitPrice: Number(
            est.totalCharges || est.estimatedCost || est.finalAmount || 0,
          ),
        },
      ];
    }
    setEditItems(mappedItems);
    const initTotalCharges = mappedItems.reduce(
      (sum, item) =>
        sum + Number(item.quantity || 1) * Number(item.unitPrice || 0),
      0,
    );
    const initDiscountAmt = (initTotalCharges * initDiscountPct) / 100;
    setEditDiscountAmount(Number(initDiscountAmt.toFixed(2)));
    setEditModalOpen(true);
  };

  const handleEditDiscountPercentChange = (val: number) => {
    const totalCharges = editItems.reduce(
      (sum, item) =>
        sum + Number(item.quantity || 1) * Number(item.unitPrice || 0),
      0,
    );
    const pct = Math.min(100, Math.max(0, val));
    setEditDiscount(pct);
    const amt = (totalCharges * pct) / 100;
    setEditDiscountAmount(Number(amt.toFixed(2)));
  };

  const handleEditDiscountAmountChange = (val: number) => {
    const totalCharges = editItems.reduce(
      (sum, item) =>
        sum + Number(item.quantity || 1) * Number(item.unitPrice || 0),
      0,
    );
    const amt = Math.min(totalCharges, Math.max(0, val));
    setEditDiscountAmount(amt);
    const calculatedPct = totalCharges > 0 ? (amt / totalCharges) * 100 : 0;
    setEditDiscount(Math.min(100, Number(calculatedPct.toFixed(2))));
  };

  const handleAddItem = () => {
    const defaultMaster = masterCptList[0] || {
      code: "99213",
      description: "Office or outpatient visit",
      averageCost: 150,
    };
    setEditItems((prev) => [
      ...prev,
      {
        code: String(defaultMaster.code || defaultMaster.cptCode || "99213"),
        description: String(
          defaultMaster.description ||
            defaultMaster.shortDescription ||
            "Medical Service",
        ),
        quantity: 1,
        unitPrice: Number(
          defaultMaster.averageCost || defaultMaster.basePrice || 150,
        ),
      },
    ]);
  };

  const handleSelectMasterCptForEditItem = (
    index: number,
    selectedCodeStr: string,
  ) => {
    const matched = masterCptList.find(
      (c: any) => String(c.code || c.cptCode) === String(selectedCodeStr),
    );
    if (matched) {
      setEditItems((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          code: String(matched.code || matched.cptCode),
          description: String(
            matched.description ||
              matched.shortDescription ||
              matched.longDescription ||
              "Medical Service",
          ),
          unitPrice: Number(
            matched.averageCost ||
              matched.basePrice ||
              next[index].unitPrice ||
              0,
          ),
        };
        return next;
      });
    }
  };

  const handleRemoveItem = (index: number) => {
    if (editItems.length <= 1) {
      toast({ title: "At least one item required", variant: "destructive" });
      return;
    }
    setEditItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    if (field === "code" || field === "description") return; // CPT code and description are locked to Master Lookup
    setEditItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const calculateEditTotals = () => {
    const totalCharges = editItems.reduce(
      (sum, item) =>
        sum + Number(item.quantity || 1) * Number(item.unitPrice || 0),
      0,
    );
    const discountPercent = Math.min(
      100,
      Math.max(0, Number(editDiscount || 0)),
    );
    const discountAmount = Math.min(
      totalCharges,
      Math.max(
        0,
        Number(
          editDiscountAmount !== undefined
            ? editDiscountAmount
            : (totalCharges * discountPercent) / 100,
        ),
      ),
    );
    const finalAmount = Math.max(0, totalCharges - discountAmount);
    return { totalCharges, discountPercent, discountAmount, finalAmount };
  };

  const handleSaveEditSubmit = async () => {
    if (!editingEstimate) return;
    const { totalCharges, discountPercent, finalAmount } =
      calculateEditTotals();
    try {
      await updateEstimateDetails.mutateAsync({
        id: editingEstimate.id,
        name: editTitle,
        items: editItems,
        totalCharges,
        discount: discountPercent,
        finalAmount,
      });
      toast({
        title: "Estimate Updated",
        description: "Estimate details and pricing breakdown have been saved.",
      });
      setEditModalOpen(false);
      setEditingEstimate(null);
    } catch (err: any) {
      toast({
        title: "Update Failed",
        description: err.message || "Failed to update estimate",
        variant: "destructive",
      });
    }
  };

  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [checkoutEstimate, setCheckoutEstimate] = useState<any>(null);
  const [checkoutForm, setCheckoutForm] = useState({
    appointmentDate: new Date(Date.now() + 86400000)
      .toISOString()
      .split("T")[0],
    startTime: "09:00 AM",
    cardNumber: "4242 •••• •••• 4242",
    cardExpiry: "12/28",
    cardCvc: "123",
    cardHolder: "",
    notes: "",
  });

  const handleOpenCheckoutModal = (est: any) => {
    setCheckoutEstimate(est);
    setCheckoutForm({
      appointmentDate: new Date(Date.now() + 86400000)
        .toISOString()
        .split("T")[0],
      startTime: "09:00 AM",
      cardNumber: "4242 •••• •••• 4242",
      cardExpiry: "12/28",
      cardCvc: "123",
      cardHolder: "",
      notes: "",
    });
    setCheckoutModalOpen(true);
  };

  const handleCompleteCheckoutSubmit = async () => {
    if (!checkoutEstimate) return;
    try {
      await updateMemberStatus.mutateAsync({
        id: checkoutEstimate.id,
        status: "COMPLETED",
        finalAmount: Number(checkoutEstimate.finalAmount),
        negotiationNote: `Paid $${Number(checkoutEstimate.finalAmount).toFixed(2)} & booked appointment for ${checkoutForm.appointmentDate} at ${checkoutForm.startTime}. ${checkoutForm.notes}`,
      });

      toast({
        title: "Payment Successful & Appointment Booked!",
        description: `Your estimate of $${Number(checkoutEstimate.finalAmount).toFixed(2)} has been paid and scheduled for ${checkoutForm.appointmentDate} at ${checkoutForm.startTime}.`,
      });

      setCheckoutModalOpen(false);
      setCheckoutEstimate(null);
    } catch (err: any) {
      toast({
        title: "Checkout Error",
        description:
          err.message || "Failed to process payment and book appointment",
        variant: "destructive",
      });
    }
  };

  const handleOpenProposalModal = (est: any) => {
    setProposalEstimate(est);
    setProposedPrice(Number(est.finalAmount || 0).toFixed(2));
    setProposedNote("");
    setProposalModalOpen(true);
  };

  const handleAcceptEstimate = async (est: any) => {
    try {
      await updateMemberStatus.mutateAsync({
        id: est.id,
        status: "ACCEPTED",
        finalAmount: Number(est.finalAmount),
        targetEstimate: est,
      });
      toast({
        title: "Estimate Accepted",
        description: "You have accepted the provider's estimate.",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to accept estimate",
        variant: "destructive",
      });
    }
  };

  const handleSubmitProposal = async () => {
    if (!proposalEstimate || !proposedPrice) return;
    try {
      const price = parseFloat(proposedPrice);
      await updateMemberStatus.mutateAsync({
        id: proposalEstimate.id,
        status: "PATIENT_PROPOSED",
        finalAmount: price,
        counterNote: proposedNote,
        targetEstimate: proposalEstimate,
      });
      toast({
        title: "Price Proposal Sent",
        description:
          "Your proposed price adjustment has been sent to the provider.",
      });
      setProposalModalOpen(false);
      setProposalEstimate(null);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to send proposal",
        variant: "destructive",
      });
    }
  };

  const handleDeleteEstimate = (id: any) => {
    setDeletingId(id);
    deleteEstimate.mutate(
      { id },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getListCostEstimatesQueryKey(),
          });
          await queryClient.invalidateQueries({
            queryKey: ["memberEstimates"],
          });
          refetchMemberEstimates();
          toast({
            title: "Estimate removed",
            description: "The estimate has been deleted.",
          });
          setDeletingId(null);
        },
        onError: (err: any) => {
          toast({
            title: "Delete Failed",
            description: err?.message || "Failed to delete estimate",
            variant: "destructive",
          });
          setDeletingId(null);
        },
      },
    );
  };

  const isMemberCreatedEstimate = (est: any) => {
    if (!est) return false;
    if (est.createdByRole === "MEMBER") return true;
    if (est.createdByRole === "PROVIDER") return false;
    const rawItems = Array.isArray(est.items) ? est.items : [];
    if (rawItems.some((item: any) => item?.createdByRole === "MEMBER"))
      return true;
    if (rawItems.some((item: any) => item?.createdByRole === "PROVIDER"))
      return false;
    if (
      est.status === "SUBMITTED" ||
      est.status === "CALCULATED" ||
      est.status === "SAVED"
    )
      return true;
    if (est.status === "PENDING" || est.status === "PROVIDER_COUNTERED")
      return false;
    return false;
  };

  const isEstimateMatchSearch = (est: any) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase().trim();
    const name = (est.name || est.title || est.procedure || "").toLowerCase();
    const providerName = (
      est.providerName ||
      est.provider?.displayName ||
      `${est.provider?.firstName || ""} ${est.provider?.lastName || ""}`
    ).toLowerCase();
    const cpt = (
      est.cptCodes ||
      (Array.isArray(est.items)
        ? est.items.map((i: any) => i.code || i.cptCode).join(", ")
        : "")
    ).toLowerCase();
    return (
      name.includes(query) ||
      providerName.includes(query) ||
      cpt.includes(query)
    );
  };

  const allApiEstimates = memberEstimates || [];

  const rawReceivedEstimates = useMemo(() => {
    return allApiEstimates.filter((est: any) => !isMemberCreatedEstimate(est));
  }, [allApiEstimates]);

  const rawMyCalculatedEstimates = useMemo(() => {
    const memberApiEstimates = allApiEstimates.filter((est: any) =>
      isMemberCreatedEstimate(est),
    );
    const localEstimates = estimates || [];
    return [...memberApiEstimates, ...localEstimates];
  }, [allApiEstimates, estimates]);

  const rawAllCombinedEstimates = useMemo(() => {
    const map = new Map();
    [...rawReceivedEstimates, ...rawMyCalculatedEstimates].forEach(
      (est: any) => {
        if (est && est.id) {
          map.set(String(est.id), est);
        }
      },
    );
    return Array.from(map.values());
  }, [rawReceivedEstimates, rawMyCalculatedEstimates]);

  const filteredEstimatesList = useMemo(() => {
    let baseList: any[] = [];
    if (activeTab === "received") {
      baseList = rawReceivedEstimates;
    } else if (activeTab === "my") {
      baseList = rawMyCalculatedEstimates;
    } else {
      baseList = rawAllCombinedEstimates;
    }

    return baseList.filter((est: any) => {
      const matchSearch = isEstimateMatchSearch(est);
      if (!matchSearch) return false;

      if (statusFilter !== "ALL") {
        const s = String(est.status || "SAVED").toUpperCase();
        if (statusFilter === "PAID") return s === "PAID" || s === "COMPLETED";
        if (statusFilter === "ACCEPTED")
          return s === "ACCEPTED" || s === "APPROVED";
        if (statusFilter === "PENDING")
          return s === "PENDING" || s === "SUBMITTED";
        if (statusFilter === "PATIENT_PROPOSED")
          return s === "PATIENT_PROPOSED";
        if (statusFilter === "PROVIDER_COUNTERED")
          return s === "PROVIDER_COUNTERED";
        if (statusFilter === "DRAFT")
          return s === "SAVED" || s === "CALCULATED" || !s;
        if (statusFilter === "REFUNDED")
          return s === "REFUNDED" || s === "CANCELLED_REFUNDED";
      }
      return true;
    });
  }, [
    activeTab,
    rawReceivedEstimates,
    rawMyCalculatedEstimates,
    rawAllCombinedEstimates,
    searchQuery,
    statusFilter,
  ]);

  const renderEstimateCard = (est: any) => {
    const isCompleted = est.status === "COMPLETED";
    const isAccepted = est.status === "ACCEPTED" || est.status === "APPROVED";
    const isPatientProposed = est.status === "PATIENT_PROPOSED";
    const isProviderCountered = est.status === "PROVIDER_COUNTERED";
    const isSubmitted = est.status === "SUBMITTED" || est.status === "PENDING";
    const isDraft =
      !est.status || est.status === "SAVED" || est.status === "CALCULATED";

    const providerDisplayName =
      est.provider?.displayName ||
      est.providerName ||
      `${est.provider?.firstName || ""} ${est.provider?.lastName || ""}`.trim() ||
      "Healthcare Provider";
    const titleName =
      est.name || est.title || est.procedure || "Custom Cost Estimate";
    const totalCharges = Number(
      est.totalCharges || est.estimatedCost || est.finalAmount || 0,
    );
    const discount = Number(est.discount || 0);
    const finalAmount = Number(
      est.finalAmount || est.patientResponsibility || est.estimatedCost || 0,
    );

    // Extract latest negotiation note if available
    let latestHistoryNote = "";
    if (Array.isArray(est.items?.history) && est.items.history.length > 0) {
      const last = est.items.history[est.items.history.length - 1];
      if (last?.note) latestHistoryNote = last.note;
    } else if (
      Array.isArray(est.negotiationHistory) &&
      est.negotiationHistory.length > 0
    ) {
      const last = est.negotiationHistory[est.negotiationHistory.length - 1];
      if (last?.note) latestHistoryNote = last.note;
    }

    return (
      <div
        key={est.id}
        className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm flex flex-col justify-between hover:border-primary/30 transition-all"
      >
        <div className="space-y-3.5">
          {/* Status Badge & Actions Meta Row */}
          <div className="flex items-center justify-between gap-2">
            <Badge
              className={
                (isPatientProposed
                  ? "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200"
                  : isProviderCountered
                    ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border-indigo-200"
                    : isSubmitted
                      ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200"
                      : isAccepted
                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200"
                        : isCompleted
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200"
                          : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300") +
                " font-bold text-[11px] sm:text-xs px-2.5 py-0.5 rounded-full border max-w-[calc(100%-2.5rem)] truncate"
              }
            >
              {isPatientProposed
                ? "Your Proposal Sent"
                : isProviderCountered
                  ? "Provider Countered"
                  : isSubmitted
                    ? "Submitted to Provider"
                    : isAccepted
                      ? "Accepted"
                      : isCompleted
                        ? "Paid & Scheduled"
                        : "Saved Draft"}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteEstimate(est.id);
              }}
              disabled={deletingId === est.id}
              className="w-7 h-7 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"
              title="Delete Estimate"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Title & Provider Row - Unconstrained 100% Width */}
          <div>
            <h3 className="font-bold text-slate-900 dark:text-white text-base leading-snug break-words">
              {titleName}
            </h3>
            <p className="text-xs text-primary font-semibold mt-1 truncate">
              From: {providerDisplayName}
            </p>
          </div>

          {/* Pricing Breakdown Box */}
          <div className="bg-slate-50 dark:bg-slate-950 p-3.5 rounded-xl border border-slate-100 dark:border-slate-800 space-y-2 text-xs">
            <div className="flex items-center justify-between text-slate-500">
              <span className="truncate pr-2">Total Standard Charges:</span>
              <span className="font-medium shrink-0">
                ${totalCharges.toFixed(2)}
              </span>
            </div>
            {discount > 0 && (
              <div className="flex items-center justify-between text-emerald-600 font-medium">
                <span className="truncate pr-2">Provider Discount:</span>
                <span className="shrink-0">{discount.toFixed(1)}%</span>
              </div>
            )}
            <div className="flex items-center justify-between font-extrabold text-slate-900 dark:text-white text-sm pt-2 border-t border-slate-200 dark:border-slate-800">
              <span className="truncate pr-2">Final Offered Price:</span>
              <span className="text-primary text-base shrink-0">
                ${finalAmount.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Status Specific Notes & Negotiation Note Display */}
          {latestHistoryNote && (
            <div className="p-3 bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/50 rounded-xl text-xs text-indigo-900 dark:text-indigo-300 flex items-start gap-2.5">
              <MessageSquare className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <span className="font-bold block text-[10px] uppercase tracking-wider text-indigo-500 mb-0.5">
                  Negotiation Note
                </span>
                <p className="italic text-slate-700 dark:text-slate-300 break-words">
                  "{latestHistoryNote}"
                </p>
              </div>
            </div>
          )}

          {isPatientProposed && !latestHistoryNote && (
            <div className="p-3 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-xl text-xs text-purple-900 dark:text-purple-300 leading-relaxed">
              ⚡ You proposed <strong>${finalAmount.toFixed(2)}</strong>.
              Awaiting provider's review.
            </div>
          )}
          {isProviderCountered && !latestHistoryNote && (
            <div className="p-3 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs text-indigo-900 dark:text-indigo-300 leading-relaxed">
              💬 Provider sent counter offer of{" "}
              <strong>${finalAmount.toFixed(2)}</strong>.
            </div>
          )}
          {isAccepted && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs text-emerald-900 dark:text-emerald-300 font-bold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                Price agreed at ${finalAmount.toFixed(2)}! Ready to checkout &
                book.
              </span>
            </div>
          )}
          {(isCompleted || String(est.status || "").toUpperCase() === "PAID") &&
            (() => {
              const paymentDetails = (est.items as any)?.paymentDetails || {};
              const redemptionCode =
                paymentDetails.redemptionCode ||
                `RED-${String(est.id).replace(/-/g, "").substring(0, 4).toUpperCase()}-9281`;
              const isRedeemed = paymentDetails.redemptionStatus === "REDEEMED";

              return (
                <div className="p-3 bg-slate-50 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-xl space-y-2.5">
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-200 shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <span>Paid & Scheduled</span>
                    </div>
                    <Badge
                      className={
                        isRedeemed
                          ? "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-[10px] shrink-0"
                          : "bg-emerald-600 text-white font-mono text-[10px] shrink-0"
                      }
                    >
                      {isRedeemed ? "REDEEMED AT CLINIC" : "VOUCHER ACTIVE"}
                    </Badge>
                  </div>

                  <div className="p-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg flex flex-wrap sm:flex-nowrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-[9px] font-semibold text-slate-500 uppercase block">
                        Redemption Code for Provider
                      </span>
                      <span className="font-mono font-extrabold text-sm text-slate-900 dark:text-white tracking-wider block truncate">
                        {redemptionCode}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenVoucherModal(est)}
                      className="h-8 text-xs font-bold text-primary border-primary/30 hover:bg-primary/10 rounded-lg gap-1 shrink-0 whitespace-nowrap ml-auto"
                    >
                      <Eye className="w-3.5 h-3.5" /> View Voucher
                    </Button>
                  </div>
                </div>
              );
            })()}
        </div>

        {/* Action & Manage Buttons */}
        <div className="pt-4 mt-4 border-t border-slate-100 dark:border-slate-800 flex flex-col gap-2">
          {isCompleted || String(est.status || "").toUpperCase() === "PAID" ? (
            (() => {
              const paymentDetails = (est.items as any)?.paymentDetails || {};
              const isRedeemed = paymentDetails.redemptionStatus === "REDEEMED";
              const isRefunded =
                paymentDetails.redemptionStatus === "CANCELLED_REFUNDED" ||
                String(est.status || "").toUpperCase() === "REFUNDED";

              if (isRefunded) {
                return (
                  <div className="p-2.5 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 rounded-xl text-center text-xs font-semibold">
                    Payment Refunded & Voucher Cancelled
                  </div>
                );
              }

              if (isRedeemed) {
                return (
                  <div className="p-2.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-xl text-center text-xs font-semibold">
                    Service Rendered & Redeemed (Locked)
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleOpenCheckoutModal(est)}
                      className="h-9 text-xs font-bold rounded-xl border-indigo-200 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 gap-1.5 whitespace-nowrap px-2"
                    >
                      <Calendar className="w-3.5 h-3.5 text-indigo-600 shrink-0" />{" "}
                      Reschedule
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleOpenRefundModal(est)}
                      className="h-9 text-xs font-bold rounded-xl border-red-200 text-red-600 dark:text-red-400 hover:bg-red-50 gap-1.5 whitespace-nowrap px-2"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500 shrink-0" />{" "}
                      Request Refund
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => handleOpenEditModal(est)}
                    className="w-full h-8 text-xs font-semibold rounded-xl border-slate-200 text-slate-700 dark:text-slate-300 hover:bg-slate-50 gap-1.5"
                  >
                    <Pencil className="w-3.5 h-3.5 text-slate-500 shrink-0" />{" "}
                    Adjust Details
                  </Button>
                </div>
              );
            })()
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => handleOpenEditModal(est)}
                className="h-9 text-xs font-bold rounded-xl border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 gap-1.5 whitespace-nowrap px-2"
              >
                <Pencil className="w-3.5 h-3.5 text-indigo-600 shrink-0" /> Edit
                Details
              </Button>

              {isDraft ? (
                <Button
                  onClick={() => handleOpenSubmitModal(est)}
                  className="h-9 text-xs font-bold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 shadow-sm whitespace-nowrap px-2"
                >
                  <Zap className="w-3.5 h-3.5 shrink-0" /> Send & Negotiate
                </Button>
              ) : (
                <Button
                  onClick={() => handleOpenProposalModal(est)}
                  className="h-9 text-xs font-bold rounded-xl bg-purple-600 hover:bg-purple-700 text-white gap-1.5 shadow-sm whitespace-nowrap px-2"
                >
                  <MessageSquare className="w-3.5 h-3.5 shrink-0" /> Propose
                  Rate
                </Button>
              )}
            </div>
          )}

          <Button
            onClick={() => {
              setHistoryEstimate(est);
              setHistoryModalOpen(true);
            }}
            variant="ghost"
            className="w-full text-slate-500 hover:text-slate-900 dark:hover:text-white font-semibold h-8 text-xs rounded-xl gap-1.5"
          >
            <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" /> View Full
            History & Details
          </Button>
        </div>
      </div>
    );
  };

  return (
    <MemberLayout>
      <div className="w-full p-4 sm:p-6 lg:p-8 flex flex-col min-h-[calc(100vh-8rem)]">
        {/* Page Header */}
        <div className="flex items-center gap-3.5 sm:gap-4 pb-5 border-b border-slate-200/80 dark:border-slate-800">
          <div className="p-3 sm:p-3.5 bg-primary/10 rounded-2xl flex-shrink-0 text-primary">
            <FileText className="w-6 h-6 sm:w-7 sm:h-7" />
          </div>
          <div className="space-y-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Estimates
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-medium">
              Manage, filter, and review all your Good Faith Estimates, provider
              quotes, and saved calculations.
            </p>
          </div>
        </div>

        {/* Navigation Tabs Bar */}
        <div className="pt-5 pb-1 overflow-x-auto scrollbar-none">
          <div className="inline-flex items-center gap-1.5 p-1.5 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200/70 dark:border-slate-700/70">
            <button
              type="button"
              onClick={() => {
                setActiveTab("all");
                setCurrentPage(1);
              }}
              className={`px-4 py-2 text-xs sm:text-sm font-bold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer shrink-0 ${
                activeTab === "all"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <FileText className="w-4 h-4 shrink-0" />
              <span>All Estimates ({rawAllCombinedEstimates.length})</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("received");
                setCurrentPage(1);
              }}
              className={`px-4 py-2 text-xs sm:text-sm font-bold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer shrink-0 ${
                activeTab === "received"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <FileText className="w-4 h-4 shrink-0" />
              <span>
                Received Provider Estimates ({rawReceivedEstimates.length})
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("my");
                setCurrentPage(1);
              }}
              className={`px-4 py-2 text-xs sm:text-sm font-bold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer shrink-0 ${
                activeTab === "my"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <Save className="w-4 h-4 shrink-0" />
              <span>
                My Saved Estimates ({rawMyCalculatedEstimates.length})
              </span>
            </button>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-3 pb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search estimates..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 w-full bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-xl text-xs sm:text-sm h-11 focus-visible:ring-primary/20"
            />
          </div>
          <div className="w-full sm:w-56 shrink-0">
            <Select
              value={statusFilter}
              onValueChange={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-full bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-xl text-xs sm:text-sm font-semibold shrink-0 h-11 cursor-pointer focus:ring-primary/20">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="ALL">All Statuses</SelectItem>
                <SelectItem value="PAID">Paid & Scheduled</SelectItem>
                <SelectItem value="ACCEPTED">Accepted</SelectItem>
                <SelectItem value="PENDING">Pending Provider Review</SelectItem>
                <SelectItem value="PATIENT_PROPOSED">
                  Patient Proposed
                </SelectItem>
                <SelectItem value="PROVIDER_COUNTERED">
                  Provider Countered
                </SelectItem>
                <SelectItem value="DRAFT">Draft / Saved</SelectItem>
                <SelectItem value="REFUNDED">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {memberEstimatesLoading || estimatesLoading ? (
          <div className="py-12 text-center text-slate-400">
            Loading estimates...
          </div>
        ) : filteredEstimatesList.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-12 text-center shadow-sm border border-slate-100 dark:border-slate-800">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-800 dark:text-white">
              No estimates found
            </h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto mt-1">
              {searchQuery || statusFilter !== "ALL"
                ? "Try adjusting your search terms or status filters."
                : "When you receive or calculate Good Faith Estimates, they will appear here for review and negotiation."}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
              {filteredEstimatesList
                .slice(
                  (currentPage - 1) * ITEMS_PER_PAGE,
                  currentPage * ITEMS_PER_PAGE,
                )
                .map(renderEstimateCard)}
            </div>

            {Math.ceil(filteredEstimatesList.length / ITEMS_PER_PAGE) > 1 && (
              <ListPagination
                currentPage={currentPage}
                totalPages={Math.ceil(
                  filteredEstimatesList.length / ITEMS_PER_PAGE,
                )}
                onPageChange={(p) => setCurrentPage(p)}
              />
            )}
          </div>
        )}

        {/* Request / Process Refund Modal */}
        <Dialog open={refundModalOpen} onOpenChange={setRefundModalOpen}>
          <DialogContent className="max-w-md rounded-2xl p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400 font-bold text-lg">
                <Trash2 className="w-5 h-5" /> Request Estimate Refund
              </DialogTitle>
            </DialogHeader>

            {refundEstimate &&
              (() => {
                const amount = Number(refundEstimate.finalAmount || 0).toFixed(
                  2,
                );
                return (
                  <div className="space-y-4 mt-2">
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      Since this service voucher has not yet been redeemed at
                      the provider clinic, you can request a full refund of{" "}
                      <strong>${amount}</strong> and cancel the appointment.
                    </p>

                    <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900 rounded-xl text-xs space-y-1">
                      <div className="flex justify-between font-semibold text-slate-800 dark:text-slate-200">
                        <span>Estimate Title:</span>
                        <span>
                          {refundEstimate.name ||
                            refundEstimate.title ||
                            "Cost Estimate"}
                        </span>
                      </div>
                      <div className="flex justify-between text-red-600 font-extrabold text-sm pt-1">
                        <span>Refund Amount:</span>
                        <span>${amount}</span>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                        Reason for Refund (Optional)
                      </Label>
                      <textarea
                        placeholder="e.g. Schedule conflict, changed healthcare provider, etc."
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        rows={2}
                        className="w-full mt-1.5 p-3 rounded-xl text-xs border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-red-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => setRefundModalOpen(false)}
                        className="rounded-xl text-xs font-semibold"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleProcessRefund}
                        disabled={updateMemberStatus.isPending}
                        className="rounded-xl font-bold bg-red-600 hover:bg-red-700 text-white shadow-md text-xs gap-1.5 px-4 h-10"
                      >
                        <Trash2 className="w-4 h-4" />
                        {updateMemberStatus.isPending
                          ? "Processing..."
                          : "Confirm & Process Refund"}
                      </Button>
                    </div>
                  </div>
                );
              })()}
          </DialogContent>
        </Dialog>

        {/* Member Service Redemption Voucher Modal */}
        <Dialog open={voucherModalOpen} onOpenChange={setVoucherModalOpen}>
          <DialogContent className="max-w-md rounded-2xl p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-primary dark:text-blue-400 font-extrabold text-lg">
                <ShieldCheck className="w-6 h-6 text-primary" /> Official
                Service Voucher
              </DialogTitle>
            </DialogHeader>

            {voucherEstimate &&
              (() => {
                const paymentDetails =
                  (voucherEstimate.items as any)?.paymentDetails || {};
                const redemptionCode =
                  paymentDetails.redemptionCode ||
                  `RED-${String(voucherEstimate.id).replace(/-/g, "").substring(0, 4).toUpperCase()}-9281`;
                const isRedeemed =
                  paymentDetails.redemptionStatus === "REDEEMED";
                const itemsList = Array.isArray(voucherEstimate.items)
                  ? voucherEstimate.items
                  : (voucherEstimate.items as any)?.items || [];
                const providerName =
                  voucherEstimate.provider?.facilityName ||
                  (voucherEstimate.provider?.firstName
                    ? `${voucherEstimate.provider.firstName} ${voucherEstimate.provider.lastName}`
                    : "Healthcare Provider");

                return (
                  <div className="space-y-4 mt-2">
                    <p className="text-xs text-slate-500">
                      Present this Redemption Voucher Code to your provider at
                      your appointment to receive your scheduled medical
                      services.
                    </p>

                    {/* Voucher Card Container */}
                    <div className="p-4 bg-slate-900 text-white rounded-2xl shadow-xl border border-slate-700/80 space-y-4 relative overflow-hidden">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                            StraightDoc Voucher
                          </span>
                          <h4 className="font-extrabold text-base text-white truncate max-w-[200px]">
                            {voucherEstimate.name ||
                              voucherEstimate.title ||
                              "Medical Cost Estimate"}
                          </h4>
                        </div>
                        <Badge
                          className={
                            isRedeemed
                              ? "bg-slate-700 text-slate-300"
                              : "bg-emerald-600 text-white font-extrabold shadow-sm"
                          }
                        >
                          {isRedeemed ? "REDEEMED" : "ACTIVE / PAID"}
                        </Badge>
                      </div>

                      {/* Voucher Code Box */}
                      <div className="p-3.5 bg-slate-950 rounded-xl border border-slate-800 text-center space-y-1">
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider block font-semibold">
                          Redemption Voucher Code
                        </span>
                        <div className="font-mono text-2xl font-black tracking-widest text-emerald-400 select-all">
                          {redemptionCode}
                        </div>
                        <span className="text-[9px] text-slate-400 block">
                          Show this code at provider check-in
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs border-t border-slate-800 pt-3">
                        <div>
                          <span className="text-[10px] text-slate-400 block">
                            Healthcare Provider
                          </span>
                          <span className="font-bold text-white truncate block">
                            {providerName}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 block">
                            Amount Paid
                          </span>
                          <span className="font-bold text-emerald-400 block">
                            $
                            {Number(voucherEstimate.finalAmount || 0).toFixed(
                              2,
                            )}
                          </span>
                        </div>
                      </div>

                      {itemsList.length > 0 && (
                        <div className="border-t border-slate-800 pt-2 text-[11px]">
                          <span className="text-[10px] text-slate-400 block mb-1">
                            Included Services & CPT Codes:
                          </span>
                          <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
                            {itemsList.map((it: any, idx: number) => (
                              <div
                                key={idx}
                                className="flex justify-between text-slate-300"
                              >
                                <span className="font-mono text-blue-400 font-bold mr-2">
                                  {it.code || it.cptCode}
                                </span>
                                <span className="truncate flex-1 text-right">
                                  {it.description || it.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(redemptionCode);
                          toast({
                            title: "Code Copied!",
                            description: `Voucher Code ${redemptionCode} copied to clipboard.`,
                          });
                        }}
                        className="rounded-xl text-xs font-bold gap-1.5 border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy Code
                      </Button>
                      <Button
                        onClick={() => window.print()}
                        className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground text-xs gap-1.5 shadow-md shadow-blue-500/20"
                      >
                        <Download className="w-3.5 h-3.5" /> Print Voucher
                      </Button>
                    </div>
                  </div>
                );
              })()}
          </DialogContent>
        </Dialog>

        {/* Send GFE to Provider & Start Negotiation Modal */}
        <Dialog open={submitModalOpen} onOpenChange={setSubmitModalOpen}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
                <Send className="w-5 h-5" /> Send GFE & Start Negotiation
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Submit this Good Faith Estimate (GFE) to the provider to
                officially begin the price negotiation process. You can include
                custom initial negotiation notes below.
              </p>

              <div className="p-3 bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900 rounded-xl text-xs space-y-1.5">
                <div className="flex justify-between font-semibold text-slate-800 dark:text-slate-200">
                  <span>Estimate Title:</span>
                  <span>
                    {submitEstimate?.name ||
                      submitEstimate?.title ||
                      "Saved Cost Estimate"}
                  </span>
                </div>
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Total Amount:</span>
                  <span className="font-bold text-indigo-600 dark:text-indigo-400">
                    $
                    {Number(
                      submitEstimate?.finalAmount ||
                        submitEstimate?.estimatedCost ||
                        submitEstimate?.totalCharges ||
                        0,
                    ).toFixed(2)}
                  </span>
                </div>
              </div>

              <div>
                <Label className="font-semibold text-slate-700 dark:text-slate-200 text-xs">
                  Initial Negotiation Note to Provider
                </Label>
                <textarea
                  placeholder="e.g. Hi Doctor, I am reviewing this Good Faith Estimate and requesting self-pay cash discount options before booking..."
                  value={submitNote}
                  onChange={(e) => setSubmitNote(e.target.value)}
                  rows={3}
                  className="w-full mt-1.5 p-3 rounded-xl text-xs border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setSubmitModalOpen(false)}
                  className="rounded-xl text-xs font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitToProvider}
                  disabled={updateMemberStatus.isPending}
                  className="rounded-xl font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md text-xs gap-1.5 px-4"
                >
                  <Send className="w-3.5 h-3.5" />
                  {updateMemberStatus.isPending
                    ? "Sending..."
                    : "Send & Begin Negotiation"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Patient Price Proposal Modal */}
        <Dialog open={proposalModalOpen} onOpenChange={setProposalModalOpen}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" /> Propose Price
                Adjustment
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-xs text-slate-500">
                Propose a adjusted price for this estimate. The provider will
                review your proposed price and accept or send a counter offer.
              </p>
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Original Charges:
                  </span>
                  <span>
                    ${Number(proposalEstimate?.totalCharges || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-primary">
                  <span>Current Provider Offer:</span>
                  <span>
                    ${Number(proposalEstimate?.finalAmount || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div>
                <Label className="font-semibold text-slate-700 dark:text-slate-200">
                  Your Proposed Price ($)
                </Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    value={proposedPrice}
                    onChange={(e) => setProposedPrice(e.target.value)}
                    className="pl-9 rounded-xl font-bold text-primary text-base"
                  />
                </div>
              </div>

              <div>
                <Label className="font-semibold text-slate-700 dark:text-slate-200">
                  Note / Reason (Optional)
                </Label>
                <Input
                  type="text"
                  placeholder="e.g. Requesting out-of-pocket adjustment..."
                  value={proposedNote}
                  onChange={(e) => setProposedNote(e.target.value)}
                  className="mt-1 rounded-xl text-xs"
                />
              </div>

              <Button
                onClick={handleSubmitProposal}
                disabled={!proposedPrice || updateMemberStatus.isPending}
                className="w-full rounded-xl font-bold bg-primary hover:bg-primary/90 text-white h-11 shadow-md"
              >
                {updateMemberStatus.isPending
                  ? "Sending Proposal..."
                  : "Submit Price Proposal"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Estimate Details Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Pencil className="w-5 h-5 text-indigo-600" /> Edit Cost
                Estimate
              </DialogTitle>
            </DialogHeader>

            {editingEstimate && (
              <div className="space-y-4 mt-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Estimate Title / Package Name
                  </Label>
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="e.g. Primary Care Consultation"
                    className="mt-1 rounded-xl text-xs"
                  />
                </div>

                {/* Items List */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label className="text-xs font-bold text-slate-800 dark:text-slate-200">
                      Service Line Items
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddItem}
                      className="h-7 text-xs font-bold text-indigo-600 border-indigo-200 hover:bg-indigo-50 rounded-lg gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Service Line
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {editItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2"
                      >
                        <div className="flex items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
                          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                            <Lock className="w-3.5 h-3.5 text-indigo-500" />
                            <span>
                              Master CPT Lookup (Code & Description Locked)
                            </span>
                          </div>
                          {masterCptList.length > 0 && (
                            <Select
                              value={item.code}
                              onValueChange={(val) =>
                                handleSelectMasterCptForEditItem(idx, val)
                              }
                            >
                              <SelectTrigger className="h-6 text-[10px] w-52 font-mono bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 rounded-md">
                                <SelectValue placeholder="Select Master CPT Code..." />
                              </SelectTrigger>
                              <SelectContent className="max-h-56">
                                {masterCptList.map((mc: any, i: number) => (
                                  <SelectItem
                                    key={i}
                                    value={String(mc.code || mc.cptCode)}
                                  >
                                    <span className="font-mono font-bold mr-1.5 text-indigo-600 dark:text-indigo-400">
                                      {mc.code || mc.cptCode}
                                    </span>
                                    <span className="text-slate-500 truncate max-w-[200px]">
                                      {mc.description || mc.shortDescription}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        <div className="grid grid-cols-12 gap-2 items-center pt-1">
                          <div className="col-span-3">
                            <Label className="text-[10px] text-slate-500">
                              Billing Code (Read-Only)
                            </Label>
                            <Input
                              value={item.code}
                              readOnly
                              disabled
                              className="mt-0.5 rounded-lg text-xs font-mono font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed select-none border-slate-200 dark:border-slate-700"
                            />
                          </div>
                          <div className="col-span-5">
                            <Label className="text-[10px] text-slate-500">
                              Master Description (Read-Only)
                            </Label>
                            <Input
                              value={item.description}
                              readOnly
                              disabled
                              className="mt-0.5 rounded-lg text-xs bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed select-none border-slate-200 dark:border-slate-700"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[10px] text-slate-500">
                              Qty
                            </Label>
                            <Input
                              type="number"
                              min={1}
                              value={item.quantity}
                              onChange={(e) =>
                                handleItemChange(
                                  idx,
                                  "quantity",
                                  Math.max(1, parseInt(e.target.value) || 1),
                                )
                              }
                              className="mt-0.5 rounded-lg text-xs font-semibold"
                            />
                          </div>
                          <div className="col-span-2 flex items-center gap-1">
                            <div className="flex-1 min-w-0">
                              <Label className="text-[10px] text-slate-500">
                                Unit Price ($)
                              </Label>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                value={item.unitPrice}
                                onChange={(e) =>
                                  handleItemChange(
                                    idx,
                                    "unitPrice",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="mt-0.5 rounded-lg text-xs font-bold"
                              />
                            </div>
                            {editItems.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveItem(idx)}
                                className="h-7 w-7 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg mt-4 shrink-0"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Adjusted (%) & Adjusted ($) Section */}
                <div className="p-4 bg-indigo-50/50 dark:bg-indigo-950/30 rounded-xl border border-indigo-100 dark:border-indigo-900/50 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                        Adjusted (%)
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step="0.1"
                          value={editDiscount}
                          onChange={(e) =>
                            handleEditDiscountPercentChange(
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          className="rounded-xl text-xs font-bold bg-white dark:bg-slate-900 pr-7"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                          %
                        </span>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                        Adjusted ($)
                      </Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          value={editDiscountAmount}
                          onChange={(e) =>
                            handleEditDiscountAmountChange(
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          className="rounded-xl text-xs font-bold bg-white dark:bg-slate-900 pl-8"
                        />
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const {
                      totalCharges,
                      discountPercent,
                      discountAmount,
                      finalAmount,
                    } = calculateEditTotals();
                    return (
                      <div className="pt-2 border-t border-indigo-100 dark:border-indigo-900/50 space-y-1 text-xs">
                        <div className="flex justify-between text-slate-600 dark:text-slate-400">
                          <span>Subtotal Charges:</span>
                          <span className="font-semibold">
                            ${totalCharges.toFixed(2)}
                          </span>
                        </div>
                        {discountAmount > 0 && (
                          <div className="flex justify-between text-emerald-600 font-semibold">
                            <span>
                              Adjusted Discount ({discountPercent.toFixed(1)}%):
                            </span>
                            <span>-${discountAmount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-extrabold text-slate-900 dark:text-white pt-1">
                          <span>Updated Final Offered Price:</span>
                          <span className="text-indigo-600 dark:text-indigo-400">
                            ${finalAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <Button
                  onClick={handleSaveEditSubmit}
                  disabled={updateEstimateDetails.isPending}
                  className="w-full rounded-xl font-bold bg-indigo-600 hover:bg-indigo-700 text-white h-11 shadow-md mt-2"
                >
                  {updateEstimateDetails.isPending
                    ? "Saving Changes..."
                    : "Save Estimate Details"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Patient Checkout & Book Estimate Modal */}
        <Dialog open={checkoutModalOpen} onOpenChange={setCheckoutModalOpen}>
          <DialogContent className="max-w-lg rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="w-5 h-5 text-emerald-600" /> Checkout &
                Book Estimate
              </DialogTitle>
            </DialogHeader>

            {checkoutEstimate && (
              <div className="space-y-4 mt-2">
                {/* Summary Banner */}
                <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-extrabold text-slate-900 dark:text-white text-base">
                        {checkoutEstimate.name ||
                          checkoutEstimate.title ||
                          "Custom Cost Estimate"}
                      </h4>
                      <p className="text-xs text-primary font-semibold mt-0.5">
                        Provider:{" "}
                        {checkoutEstimate.provider?.displayName ||
                          `${checkoutEstimate.provider?.firstName || ""} ${checkoutEstimate.provider?.lastName || ""}`.trim() ||
                          "Healthcare Provider"}
                      </p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-extrabold text-sm px-2.5 py-1 rounded-lg">
                      ${Number(checkoutEstimate.finalAmount || 0).toFixed(2)}
                    </Badge>
                  </div>

                  {checkoutEstimate.items &&
                    checkoutEstimate.items.length > 0 && (
                      <div className="pt-2 border-t border-slate-200 dark:border-slate-800 text-xs space-y-1">
                        <span className="font-semibold text-slate-500 block">
                          Included Services ({checkoutEstimate.items.length}):
                        </span>
                        {checkoutEstimate.items
                          .slice(0, 3)
                          .map((item: any, idx: number) => (
                            <div
                              key={idx}
                              className="flex justify-between text-slate-600 dark:text-slate-400"
                            >
                              <span>• {item.description || item.code}</span>
                              <span>
                                ${Number(item.unitPrice || 0).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        {checkoutEstimate.items.length > 3 && (
                          <span className="text-slate-400 italic block">
                            + {checkoutEstimate.items.length - 3} more items
                          </span>
                        )}
                      </div>
                    )}
                </div>

                {/* Appointment Scheduling */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">
                      Preferred Date *
                    </Label>
                    <div className="relative mt-1">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="date"
                        value={checkoutForm.appointmentDate}
                        onChange={(e) =>
                          setCheckoutForm({
                            ...checkoutForm,
                            appointmentDate: e.target.value,
                          })
                        }
                        className="pl-9 rounded-xl text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200">
                      Time Slot *
                    </Label>
                    <Select
                      value={checkoutForm.startTime}
                      onValueChange={(v) =>
                        setCheckoutForm({ ...checkoutForm, startTime: v })
                      }
                    >
                      <SelectTrigger className="mt-1 rounded-xl text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "08:00 AM",
                          "09:00 AM",
                          "10:30 AM",
                          "01:00 PM",
                          "02:30 PM",
                          "04:00 PM",
                        ].map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Payment Fields */}
                <div className="space-y-3 pt-1 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex items-center justify-between">
                    <Label className="font-bold text-xs text-slate-800 dark:text-slate-200">
                      Payment Information
                    </Label>
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <Lock className="w-3 h-3 text-emerald-600" /> 256-Bit SSL
                      Encrypted
                    </span>
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500">
                      Cardholder Name
                    </Label>
                    <Input
                      type="text"
                      placeholder="Jane Doe"
                      value={checkoutForm.cardHolder}
                      onChange={(e) =>
                        setCheckoutForm({
                          ...checkoutForm,
                          cardHolder: e.target.value,
                        })
                      }
                      className="mt-1 rounded-xl text-xs"
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-slate-500">
                      Card Number
                    </Label>
                    <div className="relative mt-1">
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        value={checkoutForm.cardNumber}
                        onChange={(e) =>
                          setCheckoutForm({
                            ...checkoutForm,
                            cardNumber: e.target.value,
                          })
                        }
                        className="pl-9 rounded-xl text-xs font-mono"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">
                        Expires (MM/YY)
                      </Label>
                      <Input
                        type="text"
                        value={checkoutForm.cardExpiry}
                        onChange={(e) =>
                          setCheckoutForm({
                            ...checkoutForm,
                            cardExpiry: e.target.value,
                          })
                        }
                        className="mt-1 rounded-xl text-xs font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">CVC Code</Label>
                      <Input
                        type="password"
                        maxLength={4}
                        value={checkoutForm.cardCvc}
                        onChange={(e) =>
                          setCheckoutForm({
                            ...checkoutForm,
                            cardCvc: e.target.value,
                          })
                        }
                        className="mt-1 rounded-xl text-xs font-mono"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleCompleteCheckoutSubmit}
                  disabled={
                    updateMemberStatus.isPending ||
                    !checkoutForm.appointmentDate
                  }
                  className="w-full rounded-xl font-extrabold bg-emerald-600 hover:bg-emerald-700 text-white h-11 text-sm shadow-lg shadow-emerald-600/20 mt-2"
                >
                  {updateMemberStatus.isPending
                    ? "Processing Payment & Booking..."
                    : `Pay $${Number(checkoutEstimate.finalAmount || 0).toFixed(2)} & Confirm Booking`}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Member View Negotiation History & Details Modal */}
        <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
          <DialogContent className="max-w-4xl sm:max-w-4xl lg:max-w-5xl w-[94vw] sm:w-[90vw] lg:w-[85vw] rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <History className="w-5 h-5 text-primary" /> Estimate Details &
                History
              </DialogTitle>
            </DialogHeader>
            {historyEstimate && (
              <div className="space-y-4 mt-2">
                <div className="p-3.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 space-y-1 text-xs">
                  <div className="flex justify-between font-bold text-slate-900 dark:text-white text-sm">
                    <span>
                      {historyEstimate.name ||
                        historyEstimate.title ||
                        historyEstimate.procedure ||
                        "Cost Estimate"}
                    </span>
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      {historyEstimate.status}
                    </Badge>
                  </div>
                  <p className="text-slate-500">
                    Provider:{" "}
                    {historyEstimate.provider?.displayName ||
                      "Healthcare Provider"}
                  </p>
                  <div className="flex justify-between font-black text-primary text-base pt-2 border-t border-slate-200 dark:border-slate-800">
                    <span>Current Offered Price:</span>
                    <span>
                      ${Number(historyEstimate.finalAmount || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Contextual Action Bar */}
                <div className="bg-slate-50 dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-amber-500" /> Actions &
                    Options
                  </h4>
                  <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    {String(historyEstimate.status || "").toUpperCase() !==
                      "PAID" &&
                    String(historyEstimate.status || "").toUpperCase() !==
                      "COMPLETED" ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => {
                            const est = historyEstimate;
                            setHistoryModalOpen(false);
                            handleOpenCheckoutModal(est);
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl gap-1.5 shadow-md shadow-emerald-600/20"
                        >
                          <CreditCard className="w-3.5 h-3.5" /> Pay & Book ($
                          {Number(historyEstimate.finalAmount || 0).toFixed(2)})
                        </Button>

                        {String(historyEstimate.status || "").toUpperCase() !==
                          "APPROVED" &&
                          String(historyEstimate.status || "").toUpperCase() !==
                            "ACCEPTED" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const est = historyEstimate;
                                setHistoryModalOpen(false);
                                handleAcceptEstimate(est);
                              }}
                              className="border-emerald-300 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 font-bold text-xs rounded-xl gap-1"
                            >
                              <Check className="w-3.5 h-3.5" /> Accept Price
                            </Button>
                          )}

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const est = historyEstimate;
                            setHistoryModalOpen(false);
                            handleOpenProposalModal(est);
                          }}
                          className="border-primary/40 text-primary hover:bg-primary/5 font-bold text-xs rounded-xl gap-1"
                        >
                          <MessageSquare className="w-3.5 h-3.5" /> Adjust Price
                        </Button>

                        {String(historyEstimate.status || "").toUpperCase() ===
                          "DRAFT" && (
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                setHistoryModalOpen(false);
                                await updateMemberStatus.mutateAsync({
                                  id: String(historyEstimate.id),
                                  status: "SUBMITTED",
                                  targetEstimate: historyEstimate,
                                });
                                toast({
                                  title: "Submitted to Provider!",
                                  description:
                                    "Your estimate has been submitted for review.",
                                });
                              } catch (err: any) {
                                toast({
                                  variant: "destructive",
                                  title: "Error",
                                  description:
                                    err.message || "Failed to submit estimate",
                                });
                              }
                            }}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl gap-1"
                          >
                            <Zap className="w-3.5 h-3.5" /> Submit to Provider
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const est = historyEstimate;
                            setHistoryModalOpen(false);
                            handleOpenEditModal(est);
                          }}
                          className="border-indigo-200 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 font-bold text-xs rounded-xl gap-1"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit Details
                        </Button>
                      </>
                    ) : (
                      <div className="p-2 bg-blue-50 dark:bg-blue-950/40 rounded-lg text-xs font-bold text-blue-900 dark:text-blue-200 flex items-center gap-1.5 w-full">
                        <CheckCircle2 className="w-4 h-4 text-blue-600" /> Paid
                        Invoice & Appointment Confirmed
                      </div>
                    )}
                  </div>
                </div>

                {/* Items */}
                <div>
                  <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200 mb-1 block">
                    Included Services / CPT Codes
                  </Label>
                  <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden text-xs">
                    <table className="w-full text-left">
                      <thead className="bg-slate-100 dark:bg-slate-800 text-slate-500 font-semibold border-b border-slate-200 dark:border-slate-800">
                        <tr>
                          <th className="p-2.5">Code</th>
                          <th className="p-2.5">Description</th>
                          <th className="p-2.5 text-right">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {(Array.isArray(historyEstimate.items)
                          ? historyEstimate.items
                          : []
                        ).map((item: any, idx: number) => (
                          <tr key={idx}>
                            <td className="p-2.5 font-mono font-bold text-primary">
                              {item.code || item.cptCode}
                            </td>
                            <td className="p-2.5">{item.description}</td>
                            <td className="p-2.5 text-right font-semibold">
                              ${Number(item.unitPrice || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Negotiation History Log */}
                {Array.isArray(historyEstimate.negotiationHistory) &&
                historyEstimate.negotiationHistory.length > 0 ? (
                  <div>
                    <Label className="font-semibold text-xs text-slate-700 dark:text-slate-200 mb-1.5 block">
                      Negotiation & Notes History
                    </Label>
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {historyEstimate.negotiationHistory.map(
                        (hist: any, index: number) => (
                          <div
                            key={hist.id || index}
                            className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs space-y-1"
                          >
                            <div className="flex items-center justify-between font-semibold">
                              <span
                                className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${hist.by === "PROVIDER" ? "bg-indigo-100 text-indigo-800" : "bg-purple-100 text-purple-800"}`}
                              >
                                {hist.by === "PROVIDER" ? "Provider" : "Member"}{" "}
                                ({hist.authorName})
                              </span>
                              <span className="text-[10px] text-slate-400">
                                {new Date(hist.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-slate-600 dark:text-slate-400">
                              <span>
                                Status: <strong>{hist.status}</strong>
                              </span>
                              <span className="font-bold text-primary">
                                ${Number(hist.finalAmount || 0).toFixed(2)}
                              </span>
                            </div>
                            {hist.note && (
                              <div className="p-2 bg-white dark:bg-slate-950 rounded-lg italic text-slate-700 dark:text-slate-300 mt-1 border border-slate-100 dark:border-slate-800">
                                "{hist.note}"
                              </div>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-xs text-slate-500 text-center italic">
                    No back-and-forth negotiation notes logged yet.
                  </div>
                )}

                <Button
                  variant="outline"
                  className="w-full gap-2 font-bold text-xs h-9 rounded-xl border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300"
                  onClick={() =>
                    exportEstimateToCSV({
                      title:
                        historyEstimate.name ||
                        historyEstimate.title ||
                        "cost-estimate",
                      providerName:
                        historyEstimate.provider?.displayName ||
                        "Healthcare Provider",
                      patientName: "Patient",
                      date:
                        historyEstimate.createdAt || new Date().toISOString(),
                      items: (Array.isArray(historyEstimate.items)
                        ? historyEstimate.items
                        : []
                      ).map((i: any) => ({
                        code: i.code || i.cptCode || "CUSTOM",
                        description: i.description || i.name || "Service Item",
                        quantity: i.quantity || 1,
                        unitPrice: Number(i.unitPrice || 0),
                      })),
                      totalCharges: Number(historyEstimate.totalCharges || 0),
                      discount: Number(historyEstimate.discount || 0),
                      finalAmount: Number(historyEstimate.finalAmount || 0),
                    })
                  }
                >
                  <Download className="w-4 h-4" /> Download / Export Financial
                  Record (CSV)
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MemberLayout>
  );
}
